import { FaviconProvider } from "./FaviconProvider";
import { LogManager } from "../logging/LogManager";
import { StringUtil } from "../StringUtil";

export class Favicon {
    private static readonly log = LogManager.getLogger("core.FaviconManager");

    /**
     * Changes the current favicon based on the provided key (which is resolved by the registered FaviconProvider).
     * If the resolved icon path is blank/null, reset the icon to the default.
     * @param key The key that identifies the new favicon to use.
     */
    public static setOrDefault(key: string) {
        const iconPath = FaviconProvider.get()?.getIconPath(key);
        this.log.debug("Resolved icon path: %o", iconPath);
        if (!StringUtil.isEmptyString(iconPath)) {
            Favicon.internalSet(iconPath);
        } else {
            Favicon.resetToDefault();
        }
    }

    /**
     * Changes the current favicon based on the provided key (which is resolved by the registered FaviconProvider)
     * @param key The key that identifies the new favicon to use.
     */
    public static set(key: string) {
        const iconPath = FaviconProvider.get()?.getIconPath(key);
        this.log.debug("Resolved icon path: %o", iconPath);
        this.internalSet(iconPath);
    }

    private static internalSet(iconPath: string) {
        const linkElement = this.findOrCreateLinkElement();

        // Update href to change the icon
        if (StringUtil.isEmptyString(iconPath) === false) {
            linkElement.href = iconPath;
            this.log.debug("Assigned new favicon");
        }
    }

    private static findOrCreateLinkElement(): HTMLLinkElement {
        // Look for existing favicon
        let link: HTMLLinkElement = document.querySelector("link[rel='icon']") as HTMLLinkElement;

        // If no favicon exists, create one
        if (!link) {
            this.log.debug("Creating new link element to denote the favicon");
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        } else {
            this.log.debug("Found favicon link element %o", link);
        }
        return link;
    }

    public static resetToDefault() {
        const factory = FaviconProvider.get();
        if (factory != null) {
            const iconPath = factory.defaultFaviconPath;
            this.log.debug("Assigning default favicon path: %o", iconPath);
            this.internalSet(iconPath);
        } else {
            this.log.debug("Could not assign default favicon path; no factory was provided");
        }
    }
}
